import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loanCommissionAttributionsApi,
  type CorrectLoanCommissionAttributionRequest,
} from "@/api/loanCommissionAttributions";
import { commissionKeys } from "./commissionKeys";

/**
 * One loan's frozen commission attribution (L7/U7).
 *
 * <p>Answers for a loan with NO row — `isStamped: false` — rather than
 * erroring, because that is the `attribution not stamped` population and this
 * is the only surface that can repair it.</p>
 */
export function useLoanCommissionAttribution(loanId: string | null | undefined) {
  return useQuery({
    queryKey: commissionKeys.loanAttribution(loanId ?? ""),
    queryFn: () => loanCommissionAttributionsApi.get(loanId!),
    enabled: !!loanId,
  });
}

/**
 * Correct one loan's attribution.
 *
 * <p>🔴 <b>THE INVALIDATION IS THE WHOLE `commissions` PREFIX, deliberately,
 * and a narrower one is wrong here.</b> A correction re-points every still-
 * `Suggested` share on the loan (U7) AND re-drafts the recurring months that
 * loan contributed income to — so it moves the claim detail, the All Claims
 * list, the held queue and its stat tiles, roll-up claims on OTHER loans that
 * share those months, and the attribution row itself. The server reports what
 * it moved; it does not report which claim ids, and it could not usefully — a
 * redrafted month withdraws and redraws keys across the whole pool.</p>
 */
export function useCorrectLoanCommissionAttribution(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CorrectLoanCommissionAttributionRequest) =>
      loanCommissionAttributionsApi.correct(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}
